import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import Prompt from "features/common/Prompt";
import { selectEcosystemName } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import BlockchainNetworkPreference from "features/metadesk/settings/Customization/OnChainDataSource/components/BlockchainNetworkPreference";
import TrialSetStepButton from "features/home/pages/Trial/TrialSetStepButton/TrialSetStepButton";
import {
  StyledSettingAreaTitle,
  StyledSettingAreaHint,
} from "features/metadesk/Onboarding/ServiceOnboarding/AttributionLinkSetup/AttributionLinkSetup.styles";
import { StyledSettingSupportCenterContainer } from "features/metadesk/settings/Customization/SettingSupportCenter/SettingSupportCenter.styles";
import {
  useFetchEntityConfig,
  useFetchOnchainDataSetting,
} from "features/metadesk/redux/hooks";
import { getChainList } from "features/helpers/utils";
import { useStepData } from "contexts/stepContext/Step.context";
import { alertServerError } from "features/helpers/utils";

const defaultValues = {
  blockchainNetworks: [],
};

const TrialMarketingSetStep2 = ({
  step,
  onNextStep,
  onPrevStep,
  entityName,
}) => {
  const methods = useForm({ defaultValues, mode: "onSubmit" });
  const { control, watch, handleSubmit, reset } = methods;
  const { updateOnchainDataSetting } = useFetchOnchainDataSetting();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { setStepValues, stepData } = useStepData();
  const [isPostLoading, setIsPostLoading] = useState(false);

  const ecosystemName = useSelector(selectEcosystemName);
  const chainList = getChainList(ecosystemName.toLowerCase());

  useEffect(() => {
    const stepDataChainIds = stepData.blockchainNetworks || [];

    // show first five chains in the list
    const defaultChains = chainList.slice(0, 5);

    const additionalChains = chainList.filter(
      (chain) =>
        stepDataChainIds.includes(chain.value) &&
        !defaultChains.some((c) => c.value === chain.value)
    );

    const allDisplayedChains = [...defaultChains, ...additionalChains];

    const blockchainNetworks = allDisplayedChains.map((chain, index) => {
      if (stepDataChainIds.length > 0) {
        return {
          chainId: chain.value,
          checked: stepDataChainIds.includes(chain.value),
        };
      } else {
        return {
          chainId: chain.value,
          checked: index < 2, // if no backend setting, check the first two
        };
      }
    });

    reset({ blockchainNetworks });
  }, [stepData, chainList]);

  const onSubmit = async (data) => {
    try {
      const updatedData = {
        ecosystem: ecosystemName.toLowerCase(),
        enabled: true,
        chainIds: data.blockchainNetworks
          ?.filter((c) => c.checked)
          .map((c) => c.chainId),
      };

      await updateOnchainDataSetting({
        entityName,
        body: updatedData,
      });

      onNextStep();
    } catch (err) {
      dispatch(alertServerError(err));
    }
  };
  const handlePrevStep = () => {
    const blockchainNetworks = watch("blockchainNetworks")
      .filter((c) => c.checked)
      .map((c) => c.chainId);
    setStepValues({ blockchainNetworks });
    onPrevStep();
  };

  return (
    <>
      <FormProvider {...methods}>
        <StyledSettingSupportCenterContainer>
          <Box width="500px" mt={"60px"}>
            <StyledSettingAreaTitle sx={{ textAlign: "center" }}>
              {formatMessage({
                id: "serviceOnboard.dataSource.title",
              })}
            </StyledSettingAreaTitle>
            <StyledSettingAreaHint sx={{ textAlign: "center" }}>
              {formatMessage({
                id: "serviceOnboard.dataSource.description",
              })}
            </StyledSettingAreaHint>

            <BlockchainNetworkPreference
              ecosystemName={ecosystemName}
              entityName={entityName}
              canEdit={true}
              chainList={chainList}
            />
            <TrialSetStepButton
              onSave={handleSubmit(onSubmit)}
              onBack={handlePrevStep}
              saveText="Next"
              isValid={true}
              isLoading={isPostLoading}
              display="flex"
              justifyContent="center"
            />
          </Box>
        </StyledSettingSupportCenterContainer>
        <Prompt when />
      </FormProvider>
    </>
  );
};

export default TrialMarketingSetStep2;
